import { DEFAULT_CURRENCY } from "@configs/currencies";
import { GetProductDto } from "src/services/generated-api";

type CurrencyMap = GetProductDto["priceCurrencies"];

/**
 * Read one currency out of a price map.
 *
 * Truthiness, not key presence. An admin who clears a sale price leaves the
 * key behind holding null, and a key-presence test would hand that null back
 * as if it were the price. The store filters on
 * `nOfUnits > 0 && getProductPrice(p, currency)`
 * (product-store/index.tsx:109), so returning null or 0 here removes the
 * product from that country's shop entirely, with no error anywhere. A price
 * of 0 is not a price we sell at, so it is treated the same as absent.
 */
const readCurrency = (
  map: CurrencyMap | undefined,
  currency: string
): number | null => {
  if (!map) return null;
  const value = map[currency as keyof typeof map];
  return typeof value === "number" && value > 0 ? value : null;
};

export default function getProductPrice(
  product: GetProductDto,
  currency: string
): number | null {
  if (!currency) {
    return null;
  }
  if (currency === DEFAULT_CURRENCY) {
    return product.actionPrice ? product.actionPrice : product.price;
  }

  // Sale price first, then the ordinary price — the same precedence the EUR
  // branch above applies, which is the point: these used to disagree.
  const action = readCurrency(product.actionPriceCurrencies, currency);
  if (action !== null) return action;

  const base = readCurrency(product.priceCurrencies, currency);
  if (base !== null) return base;

  // "eur-at" and friends fall back to the EUR figures.
  const currencySplit = currency.split("-")[0];
  if (currencySplit === DEFAULT_CURRENCY) {
    return product.actionPrice ? product.actionPrice : product.price;
  }
  return null;
}

export function getProductBasePrice(
  product: GetProductDto,
  currency: string
): number | null {
  if (currency === DEFAULT_CURRENCY) {
    return product.price;
  }
  return readCurrency(product.priceCurrencies, currency);
}

export function productHasActionPrice(
  product: GetProductDto,
  currency: string
): boolean {
  if (currency === DEFAULT_CURRENCY) {
    return !!product.actionPrice;
  }
  return readCurrency(product.actionPriceCurrencies, currency) !== null;
}
